import dayjs from 'dayjs';
import type {
  AudioMsgVO,
  CmdMsgVO,
  CodeMsgVO,
  ImgMsgVO,
  MsgVO,
  PhoneMsgVO,
  TxtMsgVO,
  VideoMsgVO,
} from '@core/types/im';
import { MSG_CODE_TYPE, MSG_TYPE } from './constant';

// 返回学生对应的ID
export function getStudentId(id: number) {
  return `ms:${id}`;
}

// 返回老师对应的ID
export function getTeacherId(id: number) {
  return `teacher:${id}`;
}

const INITIAL_OFFSET = '-9999px';
// 离屏执行器
// 元素本身就应该是absolute或者fixed定位
export function offscreenRunner(
  dom: HTMLElement | null | undefined,
  callback: () => any,
) {
  if (!dom) return;

  const originalDisplay = dom.style.display;
  const originalLeft = dom.style.left;
  const originalTop = dom.style.top;
  const hidden = originalDisplay === 'none';

  if (hidden) {
    dom.style.left = INITIAL_OFFSET;
    dom.style.top = INITIAL_OFFSET;
    dom.style.display = '';
  }
  const res = callback();
  if (hidden) {
    dom.style.display = originalDisplay;
    dom.style.left = originalLeft;
    dom.style.top = originalTop;
  }
  return res;
}

export function getImageSize(file: File) {
  return new Promise<{ width: number; height: number } | null>((resolve) => {
    const resolveFn = () => resolve(null);
    const reader = new FileReader();
    reader.onload = function () {
      const img = new Image();
      img.onload = function () {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        resolve({ width, height });
      };
      img.onerror = resolveFn;
      img.src = reader.result as string;
    };
    reader.onerror = resolveFn;
    reader.readAsDataURL(file);
  });
}

export const formatMessageTime = (
  timestamp: number,
  useSecond?: boolean,
): string => {
  const now = dayjs();
  const targetTimestamp = useSecond ? timestamp * 1000 : timestamp;
  const messageTime = dayjs(targetTimestamp);

  if (!messageTime.isSame(now, 'year')) {
    return messageTime.format('YYYY-MM-DD HH:mm');
  }

  if (messageTime.isSame(now, 'day')) {
    return messageTime.format('HH:mm');
  }

  const diffDays = now.diff(messageTime, 'day');
  if (diffDays < 7) {
    //星期几 + 时分
    return `${messageTime.format('dddd')} ${messageTime.format('HH:mm')}`;
  }

  return messageTime.format('MM-DD HH:mm');
};

// 是否是撤回消息的命令消息
export function isCmdRecall(msg: MsgVO) {
  return (
    msg.msgBody.contentType === 'CMD' &&
    msg.msgBody.cmdMsg.type === MSG_CODE_TYPE.RECALL
  );
}

// 是否是会话心跳的命令消息
export function isCmdSessionInfo(msg: MsgVO) {
  return (
    msg.msgBody.contentType === 'CMD' &&
    msg.msgBody.cmdMsg.type === MSG_CODE_TYPE.SESSION_INFO
  );
}

// 是否是发起语音连麦的命令消息
export function isCmdViewCommunicate(msg: MsgVO) {
  return (
    msg.msgBody.contentType === 'CMD' &&
    msg.msgBody.cmdMsg.type === MSG_CODE_TYPE.VIEW_COMMUNICATE
  );
}

// 是否是关闭语音连麦的命令消息
export function isCmdViewCommunicateLose(msg: MsgVO) {
  return (
    msg.msgBody.contentType === 'CMD' &&
    msg.msgBody.cmdMsg.type === MSG_CODE_TYPE.VIEW_COMMUNICATE_LOSE
  );
}

export function isTxtMsg(m: MsgVO): m is TxtMsgVO {
  return m.msgBody.contentType === MSG_TYPE.TXT;
}
export function isImgMsg(m: MsgVO): m is ImgMsgVO {
  return m.msgBody.contentType === MSG_TYPE.IMG;
}
export function isAudioMsg(m: MsgVO): m is AudioMsgVO {
  return m.msgBody.contentType === MSG_TYPE.AUDIO;
}
export function isVideoMsg(m: MsgVO): m is VideoMsgVO {
  return m.msgBody.contentType === MSG_TYPE.VIDEO;
}
export function isCodeMsg(m: MsgVO): m is CodeMsgVO {
  return m.msgBody.contentType === MSG_TYPE.CODE;
}
export function isPhoneMsg(m: MsgVO): m is PhoneMsgVO {
  return m.msgBody.contentType === MSG_TYPE.PHONE;
}
export function isCmdMsg(m: MsgVO): m is CmdMsgVO {
  return m.msgBody.contentType === MSG_TYPE.CMD;
}
export function isRenderMsg(m: MsgVO): m is MsgVO {
  const t = m.msgBody.contentType;
  return (
    t === MSG_TYPE.TXT ||
    t === MSG_TYPE.IMG ||
    t === MSG_TYPE.AUDIO ||
    t === MSG_TYPE.VIDEO ||
    t === MSG_TYPE.CODE ||
    t === MSG_TYPE.PHONE
  );
}

export function getMsgObject(msg: MsgVO) {
  if (isTxtMsg(msg)) {
    return msg.msgBody.txtMsg;
  } else if (isImgMsg(msg)) {
    return msg.msgBody.imgMsg;
  } else if (isAudioMsg(msg)) {
    return msg.msgBody.audioMsg;
  } else if (isVideoMsg(msg)) {
    return msg.msgBody.videoMsg;
  } else if (isCodeMsg(msg)) {
    return msg.msgBody.codeMsg;
  } else if (isCmdMsg(msg)) {
    return msg.msgBody.cmdMsg;
  }
}
export function getAudioDuration(blobOrUrl: Blob | string): Promise<number> {
  const audio = new Audio();
  const isBlob = blobOrUrl instanceof Blob;
  const objUrl = isBlob ? URL.createObjectURL(blobOrUrl) : blobOrUrl;
  audio.src = objUrl;
  console.log('还没计算时长', audio.duration);
  audio.preload = 'metadata';
  return new Promise((resolve, reject) => {
    let timer: NodeJS.Timeout | null = null;
    audio.addEventListener('loadedmetadata', () => {
      if (timer) clearTimeout(timer);
      if (isBlob) URL.revokeObjectURL(objUrl);
      audio.src = '';
      resolve(audio.duration);
    });
    timer = setTimeout(() => {
      if (isBlob) URL.revokeObjectURL(objUrl);
      audio.src = '';
      reject('获取音频时长超时');
    }, 1000);
  });
}
export function roundToOneDecimal(num: number) {
  return Math.round(num * 10) / 10;
}

export function flashBackground(dom?: HTMLElement | null, duration = 1000) {
  if (dom) {
    // 移除之前的动画类
    dom.classList.remove('flashing');
    // 强制重排以重新触发动画
    void dom.offsetWidth;
    // 添加动画类
    dom.classList.add('flashing');
    // 1秒后移除动画类
    window.setTimeout(() => dom.classList.remove('flashing'), duration);
  }
}
